/**
 * Form state ⇄ section data, and validation through @npac/rules.
 *
 * Forms keep raw strings (what the TextInputs hold) so a half-typed number or phone is
 * never lost. On save the form is turned into the rules' input shape and validated with
 * validateSection(), the same function the API runs, so phone and server always agree.
 */
import {
  GROUND_MAX,
  INFLUENCE,
  genderFromCastes,
  validateSection,
  type AgentsData,
  type DemographicsData,
  type GroundData,
  type IdentityData,
  type Issue,
  type KaryakartaData,
  type SectionName,
  type SectionStatus,
} from "@npac/rules";
import { normalizeDigits } from "./boothCode";
import type { FieldSection } from "./types";

export type Influence = (typeof INFLUENCE)[number];

export interface IdentityForm {
  mandal: string;
  panchayat: string;
  village: string;
  landmark: string;
}

export interface PersonForm {
  name: string;
  phone: string;
}

export interface AgentsForm {
  pollingAgent: PersonForm;
  coPollingAgent: PersonForm;
  /** N-PAC, 31 Oct 2026: the party's BLA-2 and the election office's BLO (both optional) */
  bla2: PersonForm;
  blo: PersonForm;
}

/** the people of the agents section, in the order the forms show them */
export const AGENT_KEYS = ["pollingAgent", "coPollingAgent", "bla2", "blo"] as const;
export type AgentKey = (typeof AGENT_KEYS)[number];

export interface WorkerRow {
  /** stable key for React lists and error mapping */
  key: string;
  slot: number;
  name: string;
  mobile: string;
  whatsapp: string;
  sameAsMobile: boolean;
  role: string;
  caste: string;
  influence: Influence | "";
}

export interface KaryakartaForm {
  rows: WorkerRow[];
}

export interface CasteRow {
  key: string;
  name: string;
  male: string;
  female: string;
  isCore: boolean;
}

export interface DemographicsForm {
  totalVoters: string;
  age: { a18_25: string; a26_40: string; a41_60: string; a60plus: string };
  castes: CasteRow[];
  otherGender: string;
}

export interface GroundForm {
  problems: string[];
  suggestions: string[];
}

export interface FormBySection {
  identity: IdentityForm;
  agents: AgentsForm;
  karyakarta: KaryakartaForm;
  demographics: DemographicsForm;
  ground: GroundForm;
}

export type AnyForm = FormBySection[FieldSection];

export const MAX_WORKERS = 20;
export const GROUND_ITEMS = GROUND_MAX;
export const AGE_KEYS = ["a18_25", "a26_40", "a41_60", "a60plus"] as const;

let keySeq = 0;
export function newKey(prefix = "k"): string {
  keySeq += 1;
  return `${prefix}${Date.now().toString(36)}${keySeq.toString(36)}`;
}

/* ---------------- empty forms ---------------- */

export function emptyForm<S extends FieldSection>(section: S): FormBySection[S] {
  const forms: FormBySection = {
    identity: { mandal: "", panchayat: "", village: "", landmark: "" },
    agents: { pollingAgent: { name: "", phone: "" }, coPollingAgent: { name: "", phone: "" }, bla2: { name: "", phone: "" }, blo: { name: "", phone: "" } },
    karyakarta: { rows: [] },
    demographics: { totalVoters: "", age: { a18_25: "", a26_40: "", a41_60: "", a60plus: "" }, castes: [], otherGender: "" },
    ground: { problems: Array(GROUND_ITEMS).fill(""), suggestions: Array(GROUND_ITEMS).fill("") },
  };
  return forms[section];
}

/** Lowest free slot 1..20, or null when all 20 are used. */
export function nextFreeSlot(rows: Pick<WorkerRow, "slot">[]): number | null {
  const used = new Set(rows.map((r) => r.slot));
  for (let s = 1; s <= MAX_WORKERS; s++) if (!used.has(s)) return s;
  return null;
}

export function newWorkerRow(rows: Pick<WorkerRow, "slot">[]): WorkerRow | null {
  const slot = nextFreeSlot(rows);
  if (slot == null) return null;
  return { key: newKey("w"), slot, name: "", mobile: "", whatsapp: "", sameAsMobile: false, role: "", caste: "", influence: "" };
}

export function newCasteRow(): CasteRow {
  return { key: newKey("c"), name: "", male: "", female: "", isCore: false };
}

/* ---------------- data → form (to edit a saved / queued section) ---------------- */

const str = (v: unknown) => (v == null ? "" : String(v));

export function dataToForm<S extends FieldSection>(section: S, data: unknown): FormBySection[S] {
  const out = emptyForm(section) as AnyForm;
  if (!data || typeof data !== "object") return out as FormBySection[S];
  switch (section) {
    case "identity": {
      const d = data as IdentityData;
      return { mandal: str(d.mandal), panchayat: str(d.panchayat), village: str(d.village), landmark: str(d.landmark) } as FormBySection[S];
    }
    case "agents": {
      const d = data as AgentsData;
      const p = (x?: { name: string; phone: string }): PersonForm => ({ name: str(x?.name), phone: str(x?.phone) });
      return { pollingAgent: p(d.pollingAgent), coPollingAgent: p(d.coPollingAgent), bla2: p(d.bla2), blo: p(d.blo) } as FormBySection[S];
    }
    case "karyakarta": {
      const d = data as KaryakartaData;
      const rows: WorkerRow[] = (d.people ?? [])
        .slice()
        .sort((a, b) => a.slot - b.slot)
        .map((p) => ({
          key: newKey("w"),
          slot: p.slot,
          name: str(p.name),
          mobile: str(p.mobile),
          whatsapp: p.whatsapp && p.whatsapp !== p.mobile ? p.whatsapp : "",
          sameAsMobile: !!p.whatsapp && p.whatsapp === p.mobile,
          role: str(p.role),
          caste: str(p.caste),
          influence: (p.influence ?? "") as Influence | "",
        }));
      return { rows } as FormBySection[S];
    }
    case "demographics": {
      const d = data as DemographicsData;
      return {
        totalVoters: str(d.totalVoters),
        age: {
          a18_25: str(d.age?.a18_25),
          a26_40: str(d.age?.a26_40),
          a41_60: str(d.age?.a41_60),
          a60plus: str(d.age?.a60plus),
        },
        castes: (d.castes ?? []).map((c) => ({ key: newKey("c"), name: str(c.name), male: str(c.male), female: str(c.female), isCore: !!c.isCore })),
        otherGender: str(d.otherGender),
      } as FormBySection[S];
    }
    case "ground": {
      const d = data as GroundData;
      const pad = (a: string[] | undefined) => {
        const x = (a ?? []).map(str).slice(0, GROUND_ITEMS);
        while (x.length < GROUND_ITEMS) x.push("");
        return x;
      };
      return { problems: pad(d.problems), suggestions: pad(d.suggestions) } as FormBySection[S];
    }
  }
  return out as FormBySection[S];
}

/* ---------------- form → rules input ---------------- */

const blank = (s: string | undefined | null) => !s || s.trim() === "";
const opt = (s: string) => (blank(s) ? undefined : s.trim());

/**
 * Parse a count field. Blank → undefined. Accepts Devanagari digits and thousands separators.
 * Anything else becomes NaN so zod reports it (and the field shows an error) instead of us guessing.
 */
export function parseCount(s: string): number | undefined {
  if (blank(s)) return undefined;
  const t = normalizeDigits(s).replace(/[,\s]/g, "");
  return /^\d+$/.test(t) ? Number(t) : NaN;
}

export interface Prepared {
  raw: unknown;
  /** for row-based sections: index in the validated array → row key in the form */
  rowKeys?: string[];
}

export function formToRaw(section: FieldSection, form: AnyForm): Prepared {
  switch (section) {
    case "identity": {
      const f = form as IdentityForm;
      return { raw: { mandal: opt(f.mandal), panchayat: opt(f.panchayat), village: opt(f.village), landmark: opt(f.landmark) } };
    }
    case "agents": {
      const f = form as AgentsForm;
      const p = (x: PersonForm) => (blank(x.name) && blank(x.phone) ? undefined : { name: x.name.trim(), phone: normalizeDigits(x.phone.trim()) });
      return { raw: { pollingAgent: p(f.pollingAgent), coPollingAgent: p(f.coPollingAgent), bla2: f.bla2 ? p(f.bla2) : undefined, blo: f.blo ? p(f.blo) : undefined } };
    }
    case "karyakarta": {
      const f = form as KaryakartaForm;
      const rows = f.rows.filter(
        (r) => !(blank(r.name) && blank(r.mobile) && blank(r.whatsapp) && blank(r.role) && blank(r.caste) && !r.influence)
      );
      return {
        rowKeys: rows.map((r) => r.key),
        raw: {
          people: rows.map((r) => {
            const mobile = normalizeDigits(r.mobile.trim());
            const whatsapp = r.sameAsMobile ? mobile : opt(normalizeDigits(r.whatsapp));
            return {
              slot: r.slot,
              name: r.name.trim(),
              mobile,
              whatsapp: whatsapp || undefined,
              role: opt(r.role),
              caste: opt(r.caste),
              influence: r.influence || undefined,
            };
          }),
        },
      };
    }
    case "demographics": {
      const f = form as DemographicsForm;
      const anyAge = AGE_KEYS.some((k) => !blank(f.age[k]));
      const age = anyAge
        ? {
            a18_25: parseCount(f.age.a18_25) ?? 0,
            a26_40: parseCount(f.age.a26_40) ?? 0,
            a41_60: parseCount(f.age.a41_60) ?? 0,
            a60plus: parseCount(f.age.a60plus) ?? 0,
          }
        : undefined;
      const rows = f.castes.filter((c) => !(blank(c.name) && blank(c.male) && blank(c.female)));
      return {
        rowKeys: rows.map((r) => r.key),
        raw: {
          totalVoters: parseCount(f.totalVoters),
          age,
          otherGender: parseCount(f.otherGender),
          castes: rows.length
            ? rows.map((c) => ({ name: c.name.trim(), male: parseCount(c.male) ?? 0, female: parseCount(c.female) ?? 0, isCore: c.isCore }))
            : undefined,
        },
      };
    }
    case "ground": {
      const f = form as GroundForm;
      const clean = (a: string[]) => a.map((s) => s.trim()).filter((s) => s.length > 0);
      return { raw: { problems: clean(f.problems), suggestions: clean(f.suggestions) } };
    }
  }
}

/* ---------------- validation ---------------- */

export interface FieldError {
  /** zod path as the API reports it, e.g. "people.2.mobile" */
  path: string;
  /** same path with row indexes replaced by form row keys, e.g. "people.w1x.mobile" */
  fieldKey: string;
  message: string;
}

export type ValidationResult =
  | { ok: true; data: unknown; status: SectionStatus; issues: Issue[] }
  | { ok: false; errors: FieldError[] };

/** Map a validated-array path back to the form row it came from. */
export function toFieldKey(path: string, rowKeys?: string[]): string {
  if (!rowKeys) return path;
  const m = /^(people|castes)\.(\d+)(\..*)?$/.exec(path);
  if (!m) return path;
  const key = rowKeys[Number(m[2])];
  return key ? `${m[1]}.${key}${m[3] ?? ""}` : path;
}

export function validateForm(section: FieldSection, form: AnyForm): ValidationResult {
  const prepared = formToRaw(section, form);
  const v = validateSection(section as SectionName, prepared.raw);
  if (!v.ok) {
    return { ok: false, errors: v.errors.map((e) => ({ ...e, fieldKey: toFieldKey(e.path, prepared.rowKeys) })) };
  }
  return { ok: true, data: v.data, status: v.status, issues: v.issues };
}

/** Map server-side 400 details ([{ path, message }]) onto the current form's rows. */
export function serverErrorsToFieldErrors(section: FieldSection, form: AnyForm, details: { path: string; message: string }[]): FieldError[] {
  const prepared = formToRaw(section, form);
  return details.map((d) => ({ ...d, fieldKey: toFieldKey(d.path, prepared.rowKeys) }));
}

/* ---------------- live totals for the demographics form ---------------- */

export interface DemographicsTotals {
  total: number | null;
  ageSum: number | null;
  ageMatches: boolean | null;
  gender: { male: number; female: number; other: number } | null;
  casteSum: number | null;
  casteMatches: boolean | null;
}

const num = (s: string) => {
  const n = parseCount(s);
  return n == null || Number.isNaN(n) ? 0 : n;
};

/**
 * Running totals while typing. Uses genderFromCastes() from @npac/rules so the numbers
 * on screen are computed exactly like the server's mismatch check.
 */
export function demographicsTotals(f: DemographicsForm): DemographicsTotals {
  const t = parseCount(f.totalVoters);
  const total = t == null || Number.isNaN(t) ? null : t;
  const anyAge = AGE_KEYS.some((k) => !blank(f.age[k]));
  const ageSum = anyAge ? AGE_KEYS.reduce((a, k) => a + num(f.age[k]), 0) : null;
  const rows = f.castes.filter((c) => !(blank(c.name) && blank(c.male) && blank(c.female)));
  const gender = genderFromCastes({
    totalVoters: total ?? 0,
    otherGender: blank(f.otherGender) ? undefined : num(f.otherGender),
    castes: rows.map((c) => ({ name: c.name || "-", male: num(c.male), female: num(c.female), isCore: c.isCore })),
  });
  const casteSum = gender ? gender.male + gender.female + gender.other : null;
  return {
    total,
    ageSum,
    ageMatches: total == null || ageSum == null ? null : ageSum === total,
    gender,
    casteSum,
    casteMatches: total == null || casteSum == null ? null : casteSum === total,
  };
}
